import React, { useState, useEffect, useCallback } from 'react';
import { OptimizedPDFViewer } from './OptimizedPDFViewer';

const IFrameFallback = ({ fileUrl, title = 'Documento PDF' }) => {
    const [zoomLevel, setZoomLevel] = useState(100);

    const adjustedUrl = `${fileUrl}#zoom=${zoomLevel}&toolbar=1&navpanes=0&scrollbar=1&view=FitH`;

    return (
        <div style={{
            width: '100%',
            minHeight: '80vh',
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
            position: 'relative'
        }}>
            {/* Controlli Zoom per Mobile */}
            <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 10,
                background: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex',
                gap: '5px'
            }}>
                <button
                    onClick={() => setZoomLevel(Math.max(50, zoomLevel - 25))}
                    style={{
                        background: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 10px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                    -
                </button>
                <span style={{
                    color: 'white',
                    fontSize: '12px',
                    alignSelf: 'center',
                    minWidth: '40px',
                    textAlign: 'center'
                }}>
                    {zoomLevel}%
                </span>
                <button
                    onClick={() => setZoomLevel(Math.min(200, zoomLevel + 25))}
                    style={{
                        background: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '5px 10px',
                        fontSize: '14px',
                        cursor: 'pointer'
                    }}
                >
                    +
                </button>
            </div>

            <iframe
                src={adjustedUrl}
                title={title}
                style={{
                    width: '100%',
                    height: '80vh',
                    border: 'none'
                }}
                onError={() => {
                    console.error('IFrame fallback also failed');
                }}
            />
        </div>
    );
};

export const RobustPDFViewer = ({
    fileUrl,
    title = 'Documento PDF',
    onLoadSuccess,
    onLoadError
}) => {
    // Always start with optimized viewer for better mobile experience
    const [viewerMode, setViewerMode] = useState('optimized');
    const [attempts, setAttempts] = useState(0);
    const [preflightStatus, setPreflightStatus] = useState('idle'); // idle | checking | ok | missing
    const [preflightError, setPreflightError] = useState(null);
    const maxAttempts = 1;

    const handlePDFError = useCallback((error) => {
        console.error('PDF Viewer Error:', error);

        if (attempts < maxAttempts && !error?.message?.includes('Worker was destroyed')) {
            console.log('Switching to iframe fallback...');
            setViewerMode('iframe');
            setAttempts(prev => prev + 1);
        } else {
            console.log('All attempts failed, showing error state');
            setViewerMode('error');
        }

        onLoadError?.(error);
    }, [attempts, maxAttempts, onLoadError]);

    const handleRetry = useCallback(() => {
        setAttempts(0);
        setViewerMode('pdf');
    }, []);

    useEffect(() => {
        // Keep optimized viewer for all devices
        setAttempts(0);
        setPreflightStatus('checking');
        setPreflightError(null);

        if (!fileUrl) {
            setPreflightStatus('missing');
            setViewerMode('error');
            return;
        }

        let cancelled = false;
        const controller = new AbortController();

        const run = async () => {
            try {
                const res = await fetch(fileUrl, { method: 'HEAD', signal: controller.signal });
                if (cancelled) return;

                const statusOk = res.ok;
                const contentType = res.headers.get('content-type') || '';
                const isPdf = contentType.toLowerCase().includes('application/pdf') || fileUrl.toLowerCase().endsWith('.pdf');

                if (!statusOk || !isPdf) {
                    setPreflightStatus('missing');
                    setPreflightError(statusOk ? 'Documento non disponibile' : `HTTP ${res.status}`);
                    setViewerMode('error');
                    onLoadError?.(new Error('PDF_NOT_AVAILABLE'));
                } else {
                    setPreflightStatus('ok');
                }
            } catch (err) {
                if (cancelled) return;
                // Alcuni server non supportano HEAD: in caso di errore riprova comunque con il viewer ottimizzato
                setPreflightStatus('ok');
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [fileUrl, onLoadError]);

    if (!fileUrl) {
        return (
            <div style={{
                padding: '3rem',
                textAlign: 'center',
                color: '#6c757d'
            }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📄</div>
                <p>Nessun documento disponibile</p>
            </div>
        );
    }

    if (viewerMode === 'error') {
        return (
            <div style={{
                padding: '3rem',
                textAlign: 'center',
                background: '#fff5f5',
                borderRadius: '12px',
                border: '1px solid #fed7d7',
                margin: '2rem'
            }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                <h3 style={{ color: '#e53e3e', marginBottom: '1rem' }}>
                    Impossibile visualizzare il PDF
                </h3>
                <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                    {preflightStatus === 'missing'
                        ? 'Il documento indicato non è stato trovato. Caricalo nuovamente dal portale tenant o aggiorna il link.'
                        : 'Il documento non può essere caricato nel browser.'}
                </p>

                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={handleRetry}
                        style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '0.7rem 1.5rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                        }}
                    >
                        🔄 Riprova
                    </button>
                    {preflightError && (
                        <div style={{ color: '#999', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                            Dettagli: {preflightError}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (viewerMode === 'iframe') {
        return <IFrameFallback fileUrl={fileUrl} title={title} />;
    }

    // Rileva se siamo su mobile per passare configurazioni specifiche
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return (
        <OptimizedPDFViewer
            fileUrl={fileUrl}
            onLoadSuccess={onLoadSuccess}
            onLoadError={handlePDFError}
            width="100%"
            height="auto"
            isMobile={isMobile}
            initialScale={isMobile ? 0.8 : 1.0}
        />
    );
};

// Mobile PDF Fix Applied - October 2025// Updated at Fri Oct  3 15:48:47 CEST 2025
